import { useForm } from "react-hook-form";
import Button from "components/base/Button";
import { useAtom } from "jotai";
import { Col, Form, Row, Card, Badge } from "react-bootstrap";
import { user_info } from "jotai/UserJotaiInstance";
import axiosInstance from "helpers/api";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Dropzone from "components/base/Dropzone";
import imageIcon from "assets/img/icons/cloud-upload.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUser, 
  faEnvelope, 
  faPhone, 
  faBriefcase,
  faFileUpload,
  faCloudUpload,
  faLightbulb,
  faSave,
  faSpinner,
  faChartLine,
  faCog
} from "@fortawesome/free-solid-svg-icons";
import { UserInfo } from "interfaces/user";

type FormData = {
  attachments: any;
  user_first_name: string;
  user_last_name: string;
  user_phone: string;
  user_email: string;
  licenseNo: string;
  mlsId: string;
  years_of_experience: number;
  closed_deal: number;
  user_description: string;
  profilePhoto: FileList;
  resume: FileList;
  brokerage_attachments: any;
};

const SettingsPersonalInfo = () => {
  const [userInfo, setUserInfo] = useAtom<UserInfo>(user_info);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>();

  const handleDropzoneFiles = (acceptedFiles: File[]) => {
    setAttachments((prevAttachments) => [...prevAttachments, ...acceptedFiles]);
  };

  const handleUpdateProfile = async (data: FormData) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("user_first_name", data.user_first_name);
    formData.append("user_last_name", data.user_last_name);
    formData.append(
      "years_of_experience",
      data.years_of_experience ? data.years_of_experience.toString() : ""
    );
    formData.append(
      "closed_deal",
      data.closed_deal ? data.closed_deal.toString() : ""
    );
    formData.append("user_description", data.user_description);

    if (data.profilePhoto && data.profilePhoto.length > 0) {
      formData.append("user_profile_photo", data.profilePhoto[0]);
    }

    if (data.resume && data.resume.length > 0) {
      formData.append("resume", data.resume[0]);
    }
    // @ts-ignore
    if (attachments && attachments.length > 0) {
      attachments.forEach((file: File) => {
        formData.append("attachments", file);
      });
    }
    try {
      const response = await axiosInstance.patch(
        `/users/${userInfo.id}`,
        formData
      );
      // @ts-ignore
      setUserInfo(response.data.data.authenticatedUser);
      // @ts-ignore
      setValue("profilePhoto", null);
      // @ts-ignore
      setValue("resume", null);

      toast.success(response.data.message);
      setAttachments([]);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }

    setLoading(false);
  };

  useEffect(() => {
    setValue("user_first_name", userInfo.user_first_name as string);
    setValue("user_last_name", userInfo.user_last_name as string);
    setValue("years_of_experience", userInfo.years_of_experience as number);
    setValue("closed_deal", userInfo.closed_deal as number);
    setValue("user_email", userInfo.user_email as string);
    setValue("user_description", userInfo.user_description as string);
  }, [userInfo, setValue]);

  return (
    <div className="mb-6">
      {/* AI-Enhanced Form Header */}
      <div 
        className="mb-5 p-4 rounded-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="d-flex align-items-center info-header-main-flex">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-3 info-header-icon-wrapper"
            style={{ 
              width: "48px", 
              height: "48px",
              background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
            }}
          >
            <FontAwesomeIcon 
              icon={faCog} 
              className="text-white" 
              size="lg"
              style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
            />
          </div>
          <div className="info-header-text-content">
            <h4 style={{
              color: '#1f2937',
              textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
              fontSize: '1.5rem',
              fontWeight: 600,
              marginBottom: '4px',
              textAlign: 'center'
            }}>
              Personal Information Intelligence
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
              AI-optimized profile management and insights
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleUpdateProfile)}>
        {/* Basic Information Section */}
        <Card 
          className="mb-4 border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.1) !important',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-3"
            style={{ 
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faUser} 
                className="me-2" 
                style={{ color: '#10b981' }}
              />
              <h6 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Basic Information
              </h6>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                    First Name <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    {...register("user_first_name", {
                      required: "First name is required",
                    })}
                    type="text"
                    placeholder="Enter First Name"
                    isInvalid={!!errors.user_first_name}
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.user_first_name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                    Last Name <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    {...register("user_last_name", {
                      required: "Last name is required",
                    })}
                    type="text"
                    placeholder="Enter Last Name"
                    isInvalid={!!errors.user_last_name}
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.user_last_name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-secondary" />
                    Email
                  </Form.Label>
                  <Form.Control
                    {...register("user_email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    type="email"
                    placeholder="Enter Email"
                    isInvalid={!!errors.user_email}
                    disabled
                    style={{
                      background: 'rgba(156, 163, 175, 0.1)',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      cursor: 'not-allowed'
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.user_email?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faPhone} className="me-2 text-secondary" />
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    {...register("user_phone")}
                    type="text"
                    placeholder="Enter Phone"
                    disabled
                    style={{
                      background: 'rgba(156, 163, 175, 0.1)',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      cursor: 'not-allowed'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Professional Information Section */}
        <Card 
          className="mb-4 border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.1) !important',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-3"
            style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faBriefcase} 
                className="me-2" 
                style={{ color: '#f59e0b' }}
              />
              <h6 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Professional Metrics
              </h6>
              <Badge 
                bg="transparent" 
                className="ms-2 fw-semibold"
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  fontSize: '10px',
                  borderRadius: '10px'
                }}
              >
                AI Tracked
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faChartLine} className="me-2 text-warning" />
                    Years Of Experience
                  </Form.Label>
                  <Form.Control
                    {...register("years_of_experience", { valueAsNumber: true })}
                    type="number"
                    placeholder="Years of Experience"
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faChartLine} className="me-2 text-warning" />
                    Closed Deals
                  </Form.Label>
                  <Form.Control
                    {...register("closed_deal", { valueAsNumber: true })}
                    type="number"
                    placeholder="Number of Closed Deals"
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </Form.Group>
              </Col>

              <Col lg={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faLightbulb} className="me-2 text-info" />
                    Professional Description
                  </Form.Label>
                  <Form.Control
                    {...register("user_description")}
                    as="textarea"
                    rows={4}
                    placeholder="Tell us about your professional background and expertise..."
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      resize: 'vertical',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* File Upload Section */}
        <Card 
          className="mb-4 border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.1) !important',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-3"
            style={{ 
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faCloudUpload} 
                className="me-2" 
                style={{ color: '#8b5cf6' }}
              />
              <h6 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Document Upload
              </h6>
              <Badge 
                bg="transparent" 
                className="ms-2 fw-semibold"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: '#8b5cf6',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  fontSize: '10px',
                  borderRadius: '10px'
                }}
              >
                AI Processing
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faFileUpload} className="me-2 text-primary" />
                    Profile Photo
                  </Form.Label>
                  <Form.Control
                    {...register("profilePhoto", {
                      validate: (files) => {
                        if (!files?.length) return true;
                        return (
                          (files[0]?.size < 10_000_000 || "Max 10MB file size") &&
                          (["image/jpeg", "image/png"].includes(files[0]?.type) ||
                            "Only JPEG/PNG allowed")
                        );
                      },
                    })}
                    type="file"
                    accept="image/*"
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {errors.profilePhoto && (
                    <Form.Text className="text-danger">
                      {errors.profilePhoto.message}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faFileUpload} className="me-2 text-primary" />
                    Resume
                  </Form.Label>
                  <Form.Control
                    {...register("resume", {
                      validate: (files) => {
                        if (!files?.length) return true;
                        return (
                          (files[0]?.size < 10_000_000 || "Max 10MB file size") &&
                          (["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(files[0]?.type) ||
                            "Only PDF/DOC/DOCX allowed")
                        );
                      },
                    })}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {errors.resume && (
                    <Form.Text className="text-danger">
                      {errors.resume.message}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col lg={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faCloudUpload} className="me-2 text-primary" />
                    Additional Attachments
                  </Form.Label>
                  <div 
                    className="p-4 rounded-3 text-center"
                    style={{
                      background: 'rgba(139, 92, 246, 0.05)',
                      border: '2px dashed rgba(139, 92, 246, 0.3)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <Dropzone
                      accept={{
                        "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                        "application/pdf": [".pdf"],
                        "application/msword": [".doc"],
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                      }}
                      uploadIcon={imageIcon}
                      isMulti
                      onDrop={handleDropzoneFiles}
                    />
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted">Selected files: {attachments.length}</small>
                      <div className="mt-2">
                        {attachments.map((file, index) => (
                          <Badge 
                            key={index}
                            bg="primary" 
                            className="me-2 mb-2"
                            style={{ fontSize: '11px' }}
                          >
                            {file.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* AI-Enhanced Submit Button */}
        <div className="text-center pt-4">
          <Button
            type="submit"
            disabled={loading}
            className="px-5 py-3 fw-semibold"
            style={{
              background: loading 
                ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' 
                : 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              border: 'none',
              borderRadius: '20px',
              fontSize: '16px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              boxShadow: loading 
                ? 'none' 
                : '0 10px 30px rgba(0, 212, 255, 0.3)',
              transition: 'all 0.3s ease',
              minWidth: '200px'
            }}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
                AI Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Update Profile
              </>
            )}
          </Button>
        </div>
      </form>

      {/* AI Enhancement Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .form-control:focus {
            border-color: rgba(0, 212, 255, 0.5) !important;
            box-shadow: 0 0 0 0.2rem rgba(0, 212, 255, 0.15) !important;
            background: rgba(255, 255, 255, 0.95) !important;
          }
          
          .btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 15px 40px rgba(0, 212, 255, 0.4) !important;
          }
          
          .form-control:hover:not(:disabled) {
            border-color: rgba(0, 212, 255, 0.4) !important;
            background: rgba(255, 255, 255, 0.9) !important;
          }
        `
      }} />

      {/* Responsive Styles for Header */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (max-width: 500px) {
            .info-header-main-flex {
              flex-direction: column;
              align-items: center; /* Center the stacked items */
            }
            .info-header-icon-wrapper {
              margin-right: 0 !important; /* Remove right margin on small screens */
              margin-bottom: 0.75rem; /* Add space between icon and text */
            }
            .info-header-text-content {
              text-align: center;
              width: 100%;
            }
          }
        `
      }} />
    </div>
  );
};

export default SettingsPersonalInfo;
